import { useState, useCallback } from "react";
import * as api from "../api";
import { clearPendingReferralCode } from "../lib/referralAttribution";

/* -----------------------------------------------------------------------------
 * useAuth — encapsulates every piece of state + every handler related to
 * authentication (login, registration, 2FA challenge/setup, forgot-password
 * OTP wizard, change-password, logout). Extracted from the previously
 * monolithic App.js.
 *
 * Cross-cutting concerns (session/orders/ui) are injected via `deps` so the
 * hook stays pure and testable — it does NOT reach into a global context.
 * --------------------------------------------------------------------------- */
export function useAuth(deps) {
  const {
    setUser, setActiveRole, setClientTab, setSelectedGame, setSelectedPackage,
    setOrders, showToast, loadOrders, referralCode,
  } = deps;

  // ---------- Local state ---------- //
  const [authMode, setAuthMode] = useState("login");
  const [authBusy, setAuthBusy] = useState(false);
  // Login
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  // Register
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regCountry, setRegCountry] = useState("+58");
  const [regPhone, setRegPhone] = useState("");
  // 2FA challenge (during login)
  const [twoFAChallenge, setTwoFAChallenge] = useState(null);
  const [twoFACode, setTwoFACode] = useState("");
  const [twoFABusy, setTwoFABusy] = useState(false);
  // Forgot password (OTP wizard)
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotCountry, setForgotCountry] = useState("+58");
  const [forgotPhone, setForgotPhone] = useState("");
  const [forgotPhoneE164, setForgotPhoneE164] = useState("");
  const [forgotCode, setForgotCode] = useState("");
  const [forgotResetToken, setForgotResetToken] = useState("");
  const [forgotUsername, setForgotUsername] = useState("");
  const [forgotNewPass, setForgotNewPass] = useState("");
  const [forgotNewPass2, setForgotNewPass2] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  // 2FA management (in Seguridad tab)
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFASetup, setTwoFASetup] = useState(null);
  const [twoFASetupCode, setTwoFASetupCode] = useState("");
  const [twoFAEnableBusy, setTwoFAEnableBusy] = useState(false);
  const [twoFADisablePwd, setTwoFADisablePwd] = useState("");
  const [twoFADisableBusy, setTwoFADisableBusy] = useState(false);
  // Change password
  const [pwdOld, setPwdOld] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);

  // ---------- Handlers ---------- //
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthBusy(true);
    try {
      const res = await api.login({ email: loginEmail, password: loginPassword });
      if (res.needs_2fa) {
        setTwoFAChallenge({ challenge_token: res.challenge_token, email: loginEmail });
        setTwoFACode("");
        showToast("Verificación 2FA", "Ingresa el código de tu app autenticadora", "success");
        return;
      }
      api.auth.setToken(res.token);
      setUser(res.user);
      setActiveRole(res.user.role === "admin" ? "admin" : "client");
      try { window.sessionStorage?.setItem("ag7_last_user", JSON.stringify(res.user)); }
      catch (e) { console.warn("cache last user failed:", e); }
      showToast(
        res.user.role === "admin" ? "Bienvenido Administrador" : "¡Bienvenido Gamer!",
        "Sesión iniciada correctamente", "success"
      );
      await loadOrders();
    } catch (err) {
      showToast("Error de acceso", err.message || "Credenciales inválidas", "error");
    } finally {
      setAuthBusy(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!twoFAChallenge) return;
    if (!/^\d{6}$/.test(twoFACode.trim())) {
      showToast("Código inválido", "Ingresa los 6 dígitos de tu app autenticadora", "error");
      return;
    }
    setTwoFABusy(true);
    try {
      const res = await api.verifyTwoFactor({ challenge_token: twoFAChallenge.challenge_token, code: twoFACode.trim() });
      api.auth.setToken(res.token);
      setUser(res.user);
      setActiveRole(res.user.role === "admin" ? "admin" : "client");
      setTwoFAChallenge(null);
      setTwoFACode("");
      showToast("Verificación exitosa", "Bienvenido de nuevo", "success");
      await loadOrders();
    } catch (err) {
      showToast("Código incorrecto", err.message || "Intenta nuevamente", "error");
    } finally {
      setTwoFABusy(false);
    }
  };

  // Helper: build a canonical +CC... phone from the split country/phone inputs.
  const normalizeForgotPhone = () => {
    let cleaned = (forgotPhone || "").replace(/[\s\-().]+/g, "");
    if (cleaned.startsWith("00")) cleaned = "+" + cleaned.slice(2);
    if (!cleaned.startsWith("+")) {
      const cc = (forgotCountry || "").trim();
      cleaned = (cc.startsWith("+") ? cc : "+" + cc.replace(/\D/g, "")) + cleaned.replace(/\D/g, "");
    }
    return /^\+\d{7,15}$/.test(cleaned) ? cleaned : null;
  };

  const closeForgot = () => {
    setShowForgotModal(false);
    setForgotStep(1);
    setForgotPhone(""); setForgotPhoneE164("");
    setForgotCode(""); setForgotResetToken(""); setForgotUsername("");
    setForgotNewPass(""); setForgotNewPass2("");
  };

  const handleForgotStep1 = async (e) => {
    e.preventDefault();
    const e164 = normalizeForgotPhone();
    if (!e164) { showToast("Teléfono inválido", "Formato internacional, ej. +584121234567", "error"); return; }
    setForgotBusy(true);
    try {
      await api.passwordResetRequest(e164);
      setForgotPhoneE164(e164);
      setForgotStep(2);
      showToast("Código enviado", "Revisa tu WhatsApp en unos segundos", "success");
    } catch (err) {
      showToast("Error", err.message || "No se pudo procesar", "error");
    } finally { setForgotBusy(false); }
  };

  const handleForgotStep2 = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(forgotCode)) { showToast("Código inválido", "Ingresa los 6 dígitos", "error"); return; }
    setForgotBusy(true);
    try {
      const res = await api.passwordResetVerify(forgotPhoneE164, forgotCode);
      setForgotResetToken(res.reset_token);
      setForgotUsername(res.username || "");
      setForgotStep(3);
    } catch (err) {
      showToast("Código incorrecto", err.message || "Verifica e intenta de nuevo", "error");
    } finally { setForgotBusy(false); }
  };

  const handleForgotStep3 = async (e) => {
    e.preventDefault();
    if (forgotNewPass.length < 4) { showToast("Contraseña muy corta", "Mínimo 4 caracteres", "error"); return; }
    if (forgotNewPass !== forgotNewPass2) { showToast("No coinciden", "Ambas contraseñas deben ser iguales", "error"); return; }
    setForgotBusy(true);
    try {
      await api.passwordResetConfirm(forgotResetToken, forgotNewPass);
      showToast("Contraseña actualizada", "Ya puedes iniciar sesión", "success");
      closeForgot();
    } catch (err) {
      showToast("Error", err.message || "No se pudo actualizar", "error");
    } finally { setForgotBusy(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const username = (regEmail || "").trim();
    if (!/^[A-Za-z0-9._-]{3,20}$/.test(username)) {
      showToast("Usuario inválido", "3-20 caracteres alfanuméricos (letras, números, . _ -)", "error");
      return;
    }
    let cleaned = (regPhone || "").replace(/[\s\-().]+/g, "");
    if (cleaned.startsWith("00")) cleaned = "+" + cleaned.slice(2);
    if (!cleaned.startsWith("+")) {
      const cc = (regCountry || "").trim();
      cleaned = (cc.startsWith("+") ? cc : "+" + cc.replace(/\D/g, "")) + cleaned.replace(/\D/g, "");
    }
    if (!/^\+\d{7,15}$/.test(cleaned)) {
      showToast("Teléfono inválido", "Formato internacional, ej. +584121234567", "error");
      return;
    }
    setAuthBusy(true);
    try {
      const res = await api.register({
        username, phone_number: cleaned, password: regPassword,
        referral_code: typeof referralCode === "function" ? referralCode() : "",
      });
      api.auth.setToken(res.token);
      setUser(res.user);
      setActiveRole("client");
      clearPendingReferralCode();
      showToast("¡Cuenta Registrada!", "Te has registrado con éxito", "success");
      await loadOrders();
    } catch (err) {
      showToast("Error de registro", err.message || "No se pudo registrar", "error");
    } finally {
      setAuthBusy(false);
    }
  };

  const loadTwoFAStatus = useCallback(async () => {
    try {
      const res = await api.twoFactorStatus();
      setTwoFAEnabled(!!res.enabled);
    } catch (err) {
      // 2FA status is optional; failure just means we render the disabled state.
      // Keep silent in production — surfacing it would clutter the console
      // for every unauthenticated page visit.
      void err;
    }
  }, []);

  const handleStartTwoFASetup = async () => {
    try {
      const res = await api.twoFactorSetup();
      setTwoFASetup({ secret: res.secret, otpauth_uri: res.otpauth_uri });
      setTwoFASetupCode("");
    } catch (err) {
      showToast("Error", err.message || "No se pudo iniciar la configuración 2FA", "error");
    }
  };

  const handleEnableTwoFA = async (e) => {
    e.preventDefault();
    const code = twoFASetupCode.trim();
    if (!/^\d{6}$/.test(code)) {
      showToast("Código inválido", "Ingresa los 6 dígitos", "error");
      return;
    }
    setTwoFAEnableBusy(true);
    try {
      await api.twoFactorEnable(code);
      setTwoFAEnabled(true);
      setTwoFASetup(null);
      setTwoFASetupCode("");
      showToast("2FA activado", "Tu cuenta ahora requiere código al iniciar sesión.", "success");
    } catch (err) {
      showToast("Código incorrecto", err.message || "No se pudo activar", "error");
    } finally {
      setTwoFAEnableBusy(false);
    }
  };

  const handleDisableTwoFA = async (e) => {
    e.preventDefault();
    if (!twoFADisablePwd) return;
    setTwoFADisableBusy(true);
    try {
      await api.twoFactorDisable(twoFADisablePwd);
      setTwoFAEnabled(false);
      setTwoFADisablePwd("");
      showToast("2FA desactivado", "El código de autenticación ya no será requerido.", "success");
    } catch (err) {
      showToast("Error", err.message || "No se pudo desactivar", "error");
    } finally {
      setTwoFADisableBusy(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwdNew.length < 8) {
      showToast("Contraseña muy corta", "Mínimo 8 caracteres", "error");
      return;
    }
    setPwdBusy(true);
    try {
      await api.changePassword(pwdOld, pwdNew);
      setPwdOld(""); setPwdNew("");
      showToast("Contraseña actualizada", "Usa la nueva contraseña en el próximo inicio de sesión.", "success");
    } catch (err) {
      showToast("Error", err.message || "No se pudo actualizar", "error");
    } finally {
      setPwdBusy(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch (e) {
      console.error("Logout call failed (clearing local state anyway)", e);
    }
    api.auth.clear();
    try { window.sessionStorage?.removeItem("ag7_last_user"); }
    catch (e) { console.warn("clear cached user failed:", e); }
    setUser(null);
    setOrders([]);
    setActiveRole("client");
    setClientTab("store");
    setSelectedGame(null);
    setSelectedPackage(null);
  };

  return {
    // State
    authMode, setAuthMode, authBusy,
    loginEmail, setLoginEmail, loginPassword, setLoginPassword,
    regEmail, setRegEmail, regPassword, setRegPassword,
    regCountry, setRegCountry, regPhone, setRegPhone,
    twoFAChallenge, setTwoFAChallenge, twoFACode, setTwoFACode, twoFABusy,
    showForgotModal, setShowForgotModal,
    forgotStep, setForgotStep,
    forgotCountry, setForgotCountry, forgotPhone, setForgotPhone, forgotPhoneE164,
    forgotCode, setForgotCode, forgotUsername,
    forgotNewPass, setForgotNewPass, forgotNewPass2, setForgotNewPass2, forgotBusy,
    twoFAEnabled, twoFASetup, setTwoFASetup, twoFASetupCode, setTwoFASetupCode, twoFAEnableBusy,
    twoFADisablePwd, setTwoFADisablePwd, twoFADisableBusy,
    pwdOld, setPwdOld, pwdNew, setPwdNew, pwdBusy,
    // Handlers
    handleLogin, handleVerify2FA,
    closeForgot, handleForgotStep1, handleForgotStep2, handleForgotStep3,
    handleRegister,
    loadTwoFAStatus, handleStartTwoFASetup, handleEnableTwoFA, handleDisableTwoFA,
    handleChangePassword, handleLogout,
  };
}
